'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { DirectoryProvider } from '@/components/directory/DirectoryContext';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';
import { FolderOpen } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import MultiSelect from '@/components/ui/multi-select';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import { LazySection } from '@/components/ui/LazySection';
import ProductSection from './ProductSection';
import ProductActiveFilters from './ProductActiveFilters';
import { Breadcrumb } from '@/components/ui/breadcrumb';

function getDisplayValue(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  if (obj.Name) return obj.Name;
  if (obj.name) return obj.name;
  const firstString = Object.values(obj).find(v => typeof v === 'string');
  if (firstString) return String(firstString);
  if (obj.id) return obj.id;
  return '[object]';
}

export default function ProductsPage() {
  // Fetch all Products
  const { data, isLoading, isError } = useDynamicAirtableRecords('PRODUCTS');
  // Fetch schema for dynamic field detection
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => setSchema(data))
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Find the schema for the current table
  const currentTableSchema = useMemo(
    () => schema?.tables?.find((t: any) => t.name === 'Products' || t.id === 'PRODUCTS'),
    [schema]
  );

  // Helper to extract fields for a Product record
  function extractFields(product: any) {
    if (!product || !currentTableSchema) return {};
    // Use schema's primaryFieldId for the name
    const primaryFieldId = currentTableSchema.primaryFieldId;
    const primaryFieldName = currentTableSchema.fields.find((f: any) => f.id === primaryFieldId)?.name || 'Product Name';
    // Find description field
    const descriptionField = currentTableSchema.fields.find((f: any) => f.name.toLowerCase().includes('description') || f.name.toLowerCase().includes('summary'))?.name;
    // Find website field
    const fieldsToShow = Object.keys(product);
    const websiteField = fieldsToShow.find(f => f.toLowerCase().includes('website'));
    // Find location field
    const locationField = fieldsToShow.find(f => f.toLowerCase() === 'location');
    // Slug field
    const slugField = fieldsToShow.find(f => f.toLowerCase() === 'slug');

    // Extract values
    const attachments = (product['Logo'] && Array.isArray(product['Logo']))
      ? parseAttachments(product['Logo']) ?? []
      : [];
    const logo = attachments.length > 0 ? attachments[0] : null;
    const logoSrc = logo && typeof logo === 'object' ? (logo.thumbnailLarge ?? logo.url ?? '') : '';
    
    // Extract Provider Logo
    const providerLogoAttachments = (product['Provider Logo'] && Array.isArray(product['Provider Logo']))
      ? parseAttachments(product['Provider Logo']) ?? []
      : [];
    const providerLogo = providerLogoAttachments.length > 0 ? providerLogoAttachments[0] : null;
    const providerLogoSrc = providerLogo && typeof providerLogo === 'object' ? (providerLogo.thumbnailLarge ?? providerLogo.url ?? '') : '';
    
    // Extract product image
    const getImageSrc = (fieldName: string) => {
      const attachments = (product[fieldName] && Array.isArray(product[fieldName]))
        ? parseAttachments(product[fieldName]) ?? []
        : [];
      const image = attachments.length > 0 ? attachments[0] : null;
      return image && typeof image === 'object' ? (image.thumbnailLarge ?? image.url ?? '') : '';
    };

    const productImageSrc = getImageSrc('Product Image') || 
                           getImageSrc('Image') || 
                           getImageSrc('Logo') || '';
    
    const name = product[primaryFieldName] || '';
    const description = descriptionField ? product[descriptionField] : '';
    const website = websiteField ? product[websiteField] : undefined;
    let websiteUrl = website;
    if (website && typeof website === 'string' && !/^https?:\/\//i.test(website)) websiteUrl = 'https://' + website;
    let location = locationField ? product[locationField] : undefined;
    if (!location) {
      const city = product['City'] || '';
      const state = product['State'] || '';
      const country = product['Country'] || '';
      location = [city, state, country].filter(Boolean).join(', ');
    }
    const slug = slugField ? product[slugField] : undefined;
    
    // Extract product type for routing
    const productType = product['Product Type'];
    let category = 'instruments'; // default fallback
    if (productType) {
      const productTypeArray = Array.isArray(productType) ? productType : [productType];
      const firstType = productTypeArray[0];
      if (firstType) {
        switch (firstType) {
          case 'Assay Kits':
            category = 'assay-kits';
            break;
          case 'Assay Panels':
            category = 'assay-panels';
            break;
          case 'Instruments':
            category = 'instruments';
            break;
          case 'Software':
            category = 'software';
            break;
          default:
            // Fallback to keyword matching
            if (firstType.toLowerCase().includes('assay kit') || firstType.toLowerCase().includes('kit')) {
              category = 'assay-kits';
            } else if (firstType.toLowerCase().includes('assay panel') || firstType.toLowerCase().includes('panel')) {
              category = 'assay-panels';
            } else if (firstType.toLowerCase().includes('instrument') || firstType.toLowerCase().includes('microscope') || firstType.toLowerCase().includes('analyzer')) {
              category = 'instruments';
            } else if (firstType.toLowerCase().includes('software') || firstType.toLowerCase().includes('platform')) {
              category = 'software';
            }
        }
      }
    }
    
    return { logoSrc, providerLogoSrc, productImageSrc, name, description, website, websiteUrl, location, slug, category };
  }

  // Sort Products alphabetically by name
  const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
    const nameA = (extractFields(a).name || '').toLowerCase();
    const nameB = (extractFields(b).name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  }) : [];





  // Fetch all Technology records for the filter dropdown
  const { data: technologyRecords, isLoading: techLoading } = useDynamicAirtableRecords('TECHNOLOGY');
  const technologyLookup = useMemo(() => {
    if (!technologyRecords || !Array.isArray(technologyRecords)) return {};
    const map: Record<string, string> = {};
    technologyRecords.forEach((rec: any) => {
      const name = rec['Technology Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [technologyRecords]);

  // Fetch all Platform records for the filter dropdown
  const { data: platformRecords, isLoading: platformLoading } = useDynamicAirtableRecords('PLATFORMS');
  const platformLookup = useMemo(() => {
    if (!platformRecords || !Array.isArray(platformRecords)) return {};
    const map: Record<string, string> = {};
    platformRecords.forEach((rec: any) => {
      const name = rec['Platform Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [platformRecords]);

  // Fetch all Providers records for the filter dropdown
  const { data: providerRecords, isLoading: providerLoading } = useDynamicAirtableRecords('PROVIDERS');
  const providerLookup = useMemo(() => {
    if (!providerRecords || !Array.isArray(providerRecords)) return {};
    const map: Record<string, string> = {};
    providerRecords.forEach((rec: any) => {
      const name = rec['Company'] || rec['Provider'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [providerRecords]);

  // Extract unique countries from Products data
  const countryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const countries = new Set<string>();
    data.forEach((product: any) => {
      const countryField = product['Country'];
      if (Array.isArray(countryField)) {
        countryField.forEach((c) => { if (c) countries.add(c); });
      } else if (typeof countryField === 'string' && countryField) {
        countries.add(countryField);
      }
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique providers from Products data
  const providerOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !providerLookup) return [];
    const providers = new Set<string>();
    data.forEach((product: any) => {
      const providerField = product['Provider'];
      if (Array.isArray(providerField)) {
        providerField.forEach((p) => { 
          if (p) {
            const providerName = providerLookup[p] || p;
            providers.add(providerName);
          }
        });
      } else if (typeof providerField === 'string' && providerField) {
        const providerName = providerLookup[providerField] || providerField;
        providers.add(providerName);
      }
    });
    return Array.from(providers).sort((a, b) => a.localeCompare(b));
  }, [data, providerLookup]);

  // Extract unique technologies from Products data using the lookup
  const technologyOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !technologyLookup) return [];
    const technologies = new Set<string>();
    data.forEach((product: any) => {
      const technologyField = product['Technology'];
      if (Array.isArray(technologyField)) {
        technologyField.forEach((tech) => { 
          if (tech) {
            // Handle both string values and object references
            const techId = typeof tech === 'string' ? tech : (tech.id || tech);
            const techName = technologyLookup[techId] || techId;
            if (techName) technologies.add(techName);
          }
        });
      } else if (technologyField) {
        // Handle single value
        const techId = typeof technologyField === 'string' ? technologyField : (technologyField.id || technologyField);
        const techName = technologyLookup[techId] || techId;
        if (techName) technologies.add(techName);
      }
    });
    return Array.from(technologies).sort((a, b) => a.localeCompare(b));
  }, [data, technologyLookup]);

  // Extract unique platforms from Products data using the lookup
  const platformOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !platformLookup) return [];
    const platforms = new Set<string>();
    data.forEach((product: any) => {
      const platformField = product['Platform'];
      if (Array.isArray(platformField)) {
        platformField.forEach((platform) => { 
          if (platform) {
            // Handle both string values and object references
            const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
            const platformName = platformLookup[platformId] || platformId;
            if (platformName) platforms.add(platformName);
          }
        });
      } else if (platformField) {
        // Handle single value
        const platformId = typeof platformField === 'string' ? platformField : (platformField.id || platformField);
        const platformName = platformLookup[platformId] || platformId;
        if (platformName) platforms.add(platformName);
      }
    });
    return Array.from(platforms).sort((a, b) => a.localeCompare(b));
  }, [data, platformLookup]);

  // Extract unique categories from Products data
  const categoryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const categories = new Set<string>();
    data.forEach((product: any) => {
      const categoryField = product['Category'];
      if (Array.isArray(categoryField)) {
        categoryField.forEach((c) => { if (c) categories.add(c); });
      } else if (typeof categoryField === 'string' && categoryField) {
        categories.add(categoryField);
      }
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique applications from Products data
  const applicationOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const applications = new Set<string>();
    data.forEach((product: any) => {
      const applicationField = product['Application'];
      if (Array.isArray(applicationField)) {
        applicationField.forEach((app) => { if (app) applications.add(app); });
      } else if (typeof applicationField === 'string' && applicationField) {
        applications.add(applicationField);
      }
    });
    return Array.from(applications).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique sample types from Products data
  const sampleTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const sampleTypes = new Set<string>();
    data.forEach((product: any) => {
      const sampleTypeField = product['Sample Types'];
      if (Array.isArray(sampleTypeField)) {
        sampleTypeField.forEach((type) => { if (type) sampleTypes.add(type); });
      } else if (typeof sampleTypeField === 'string' && sampleTypeField) {
        sampleTypes.add(sampleTypeField);
      }
    });
    return Array.from(sampleTypes).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique plex types from Products data
  const plexTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const plexTypes = new Set<string>();
    data.forEach((product: any) => {
      const plexTypeField = product['Plex Type'];
      if (Array.isArray(plexTypeField)) {
        plexTypeField.forEach((type) => { if (type) plexTypes.add(type); });
      } else if (typeof plexTypeField === 'string' && plexTypeField) {
        plexTypes.add(plexTypeField);
      }
    });
    return Array.from(plexTypes).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique readout from Products data
  const readoutOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const readouts = new Set<string>();
    data.forEach((product: any) => {
      const readoutField = product['Readout'];
      if (Array.isArray(readoutField)) {
        readoutField.forEach((readout) => { if (readout) readouts.add(readout); });
      } else if (typeof readoutField === 'string' && readoutField) {
        readouts.add(readoutField);
      }
    });
    return Array.from(readouts).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique throughput from Products data
  const throughputOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const throughputs = new Set<string>();
    data.forEach((product: any) => {
      const throughputField = product['Throughput'];
      if (Array.isArray(throughputField)) {
        throughputField.forEach((throughput) => { if (throughput) throughputs.add(throughput); });
      } else if (typeof throughputField === 'string' && throughputField) {
        throughputs.add(throughputField);
      }
    });
    return Array.from(throughputs).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique cellular resolution from Products data
  const cellularResolutionOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const resolutions = new Set<string>();
    data.forEach((product: any) => {
      const resolutionField = product['Cellular Resolution'];
      if (Array.isArray(resolutionField)) {
        resolutionField.forEach((resolution) => { if (resolution) resolutions.add(resolution); });
      } else if (typeof resolutionField === 'string' && resolutionField) {
        resolutions.add(resolutionField);
      }
    });
    return Array.from(resolutions).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique slide resolution from Products data
  const slideResolutionOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const resolutions = new Set<string>();
    data.forEach((product: any) => {
      const resolutionField = product['Slide Resolution'];
      if (Array.isArray(resolutionField)) {
        resolutionField.forEach((resolution) => { if (resolution) resolutions.add(resolution); });
      } else if (typeof resolutionField === 'string' && resolutionField) {
        resolutions.add(resolutionField);
      }
    });
    return Array.from(resolutions).sort((a, b) => a.localeCompare(b));
  }, [data]);



  // Extract unique chemistry from Products data
  const chemistryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const chemistries = new Set<string>();
    data.forEach((product: any) => {
      const chemistryField = product['Chemistry'];
      if (Array.isArray(chemistryField)) {
        chemistryField.forEach((chemistry) => { if (chemistry) chemistries.add(chemistry); });
      } else if (typeof chemistryField === 'string' && chemistryField) {
        chemistries.add(chemistryField);
      }
    });
    return Array.from(chemistries).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique licence types from Products data
  const licenceTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const licenceTypes = new Set<string>();
    data.forEach((product: any) => {
      const licenceTypeField = product['Licence Type'];
      if (Array.isArray(licenceTypeField)) {
        licenceTypeField.forEach((licenceType) => { if (licenceType) licenceTypes.add(licenceType); });
      } else if (typeof licenceTypeField === 'string' && licenceTypeField) {
        licenceTypes.add(licenceTypeField);
      }
    });
    return Array.from(licenceTypes).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique interfaces from Products data
  const interfaceOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const interfaces = new Set<string>();
    data.forEach((product: any) => {
      const interfaceField = product['Interface'];
      if (Array.isArray(interfaceField)) {
        interfaceField.forEach((interfaceType) => { if (interfaceType) interfaces.add(interfaceType); });
      } else if (typeof interfaceField === 'string' && interfaceField) {
        interfaces.add(interfaceField);
      }
    });
    return Array.from(interfaces).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Group products by main product type
  const groupedByProductType = useMemo(() => {
    if (!data || !Array.isArray(data)) return {};
    
    const groups: Record<string, any[]> = {
      'Assay Kits': [],
      'Assay Panels': [],
      'Instruments': [],
      'Software': []
    };
    

    
    data.forEach((product: any) => {
      const productType = product['Product Type'];
      
      if (productType) {
        const productTypeArray = Array.isArray(productType) ? productType : [productType];
        
        productTypeArray.forEach((type: string) => {
          if (type && type.trim()) {
            // Direct mapping based on exact Product Type values
            switch (type) {
              case 'Assay Kits':
                groups['Assay Kits'].push(product);
                break;
              case 'Assay Panels':
                groups['Assay Panels'].push(product);
                break;
              case 'Instruments':
                groups['Instruments'].push(product);
                break;
              case 'Software':
                groups['Software'].push(product);
                break;
              default:
                // Fallback to keyword matching for any unexpected values
                if (type.toLowerCase().includes('assay kit') || type.toLowerCase().includes('kit')) {
                  groups['Assay Kits'].push(product);
                } else if (type.toLowerCase().includes('assay panel') || type.toLowerCase().includes('panel')) {
                  groups['Assay Panels'].push(product);
                } else if (type.toLowerCase().includes('instrument') || type.toLowerCase().includes('microscope') || type.toLowerCase().includes('analyzer')) {
                  groups['Instruments'].push(product);
                } else if (type.toLowerCase().includes('software') || type.toLowerCase().includes('platform')) {
                  groups['Software'].push(product);
                }
            }
          }
        });
      }
    });
    
    return groups;
  }, [data]);

  // Get subtype options for each product type
  const getSubtypeOptions = (productType: string) => {
    const products = groupedByProductType[productType] || [];
    const subtypes = new Set<string>();
    
    products.forEach((product: any) => {
      let subtypeField: any;
      
      switch (productType) {
        case 'Assay Kits':
        case 'Assay Panels':
          subtypeField = product['Assay Type'];
          break;
        case 'Instruments':
          subtypeField = product['Instrument Type'];
          break;
        case 'Software':
          subtypeField = product['Software Type'];
          break;
        default:
          return;
      }
      
      if (Array.isArray(subtypeField)) {
        subtypeField.forEach((subtype: string) => {
          if (subtype && subtype.trim()) subtypes.add(subtype);
        });
      } else if (typeof subtypeField === 'string' && subtypeField) {
        subtypes.add(subtypeField);
      }
    });
    
    return Array.from(subtypes).sort((a, b) => a.localeCompare(b));
  };

  // Add state for view mode (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Add state for sorting
  const [sortBy, setSortBy] = useState<string>('name-asc');

  // State for each product type's selected subtype
  const [selectedAssayType, setSelectedAssayType] = useState<string>('');
  const [selectedAssayPanelType, setSelectedAssayPanelType] = useState<string>('');
  const [selectedInstrumentType, setSelectedInstrumentType] = useState<string>('');
  const [selectedSoftwareType, setSelectedSoftwareType] = useState<string>('');

  // State for Assay Kits custom filters
  const [selectedSampleTypes, setSelectedSampleTypes] = useState<string[]>([]);
  const [selectedPlexTypes, setSelectedPlexTypes] = useState<string[]>([]);
  const [selectedReadout, setSelectedReadout] = useState<string[]>([]);
  const [selectedThroughput, setSelectedThroughput] = useState<string[]>([]);
  const [selectedCellularResolution, setSelectedCellularResolution] = useState<string[]>([]);
  const [selectedSlideResolution, setSelectedSlideResolution] = useState<string[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<string[]>([]);
  const [selectedChemistry, setSelectedChemistry] = useState<string[]>([]);
  const [selectedTechnology, setSelectedTechnology] = useState<string[]>([]);

  // State for Software-specific filters
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedLicenceTypes, setSelectedLicenceTypes] = useState<string[]>([]);
  const [selectedInterfaces, setSelectedInterfaces] = useState<string[]>([]);

  // State for Assay Panels custom filters
  const [selectedAssayPanelProviders, setSelectedAssayPanelProviders] = useState<string[]>([]);
  const [selectedAssayPanelSampleTypes, setSelectedAssayPanelSampleTypes] = useState<string[]>([]);
  const [selectedAssayPanelPlexTypes, setSelectedAssayPanelPlexTypes] = useState<string[]>([]);
  const [selectedAssayPanelReadout, setSelectedAssayPanelReadout] = useState<string[]>([]);
  const [selectedAssayPanelThroughput, setSelectedAssayPanelThroughput] = useState<string[]>([]);
  const [selectedAssayPanelCellularResolution, setSelectedAssayPanelCellularResolution] = useState<string[]>([]);
  const [selectedAssayPanelSlideResolution, setSelectedAssayPanelSlideResolution] = useState<string[]>([]);
  const [selectedAssayPanelApplication, setSelectedAssayPanelApplication] = useState<string[]>([]);
  const [selectedAssayPanelChemistry, setSelectedAssayPanelChemistry] = useState<string[]>([]);
  const [selectedAssayPanelTechnology, setSelectedAssayPanelTechnology] = useState<string[]>([]);

  // State for Instruments custom filters
  const [selectedInstrumentProviders, setSelectedInstrumentProviders] = useState<string[]>([]);
  const [selectedInstrumentSampleTypes, setSelectedInstrumentSampleTypes] = useState<string[]>([]);
  const [selectedInstrumentPlexTypes, setSelectedInstrumentPlexTypes] = useState<string[]>([]);
  const [selectedInstrumentReadout, setSelectedInstrumentReadout] = useState<string[]>([]);
  const [selectedInstrumentThroughput, setSelectedInstrumentThroughput] = useState<string[]>([]);
  const [selectedInstrumentCellularResolution, setSelectedInstrumentCellularResolution] = useState<string[]>([]);
  const [selectedInstrumentSlideResolution, setSelectedInstrumentSlideResolution] = useState<string[]>([]);
  const [selectedInstrumentApplication, setSelectedInstrumentApplication] = useState<string[]>([]);
  const [selectedInstrumentChemistry, setSelectedInstrumentChemistry] = useState<string[]>([]);
  const [selectedInstrumentTechnology, setSelectedInstrumentTechnology] = useState<string[]>([]);

  // State for Software custom filters
  const [selectedSoftwareProviders, setSelectedSoftwareProviders] = useState<string[]>([]);
  const [selectedSoftwarePlatforms, setSelectedSoftwarePlatforms] = useState<string[]>([]);
  const [selectedSoftwareLicenceTypes, setSelectedSoftwareLicenceTypes] = useState<string[]>([]);
  const [selectedSoftwareInterfaces, setSelectedSoftwareInterfaces] = useState<string[]>([]);

  // Set initial subtypes when data is available
  useEffect(() => {
    if (data && Array.isArray(data)) {
      // Set initial assay type to "All"
      if (!selectedAssayType) {
        setSelectedAssayType('All');
      }
      
      // Set initial assay panel type to "All"
      if (!selectedAssayPanelType) {
        setSelectedAssayPanelType('All');
      }
      
      // Set initial instrument type to "All"
      if (!selectedInstrumentType) {
        setSelectedInstrumentType('All');
      }
      
      // Set initial software type to "All"
      if (!selectedSoftwareType) {
        setSelectedSoftwareType('All');
      }
    }
  }, [data, selectedAssayType, selectedAssayPanelType, selectedInstrumentType, selectedSoftwareType]);

  // Loading/Error states
  if (isLoading || schemaLoading) {
    return (
      <DirectoryProvider>
        <div className="flex min-h-[60vh] items-center justify-center">
          <LoadingPattern patternName="cell-pattern" size="md" />
        </div>
      </DirectoryProvider>
    );
  }
  if (isError) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20 text-red-500">Error loading Products.</div>
      </DirectoryProvider>
    );
  }
  if (!data || !Array.isArray(data) || !currentTableSchema) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20">No Products found.</div>
      </DirectoryProvider>
    );
  }



  return (
    <DirectoryProvider>
      <div className="min-h-screen bg-neutral-50">
        {/* Hero Header */}
                  <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none">
          <div className="max-w-7xl mx-auto w-full h-full flex flex-col md:flex-row md:items-center px-6">
            <div className="w-full">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                  <span className="text-xl md:text-2xl lg:text-3xl font-medium flex items-center gap-2">
                    <img 
                      src="/LabAtlas-Logo-White-Full.png" 
                      alt="LabAtlas Logo" 
                      className="h-6 w-6 md:h-8 md:w-8 object-contain"
                    />
                    LabAtlas
                  </span>
                Spatial Biology Products
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Browse and filter spatial biology products — assays, instruments, and software — by provider, technology, platform, or application. Easily compare product specifications and find the right solutions for your research.
              </p>
              </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'Products', isCurrentPage: true }
              ]} 
            />
          </div>

          {/* Filter Section */}
          <LazySection minHeight={60}>
            <div className="w-full mb-6">
              {/* Filter Grid - Responsive */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                <MultiSelect
                  options={providerOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedProviders}
                  onChange={setSelectedProviders}
                  placeholder="Provider"
                />
                <MultiSelect
                  options={sampleTypeOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedSampleTypes}
                  onChange={setSelectedSampleTypes}
                  placeholder="Sample Types"
                />
                <MultiSelect
                  options={plexTypeOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedPlexTypes}
                  onChange={setSelectedPlexTypes}
                  placeholder="Plex Types"
                />
                <MultiSelect
                  options={readoutOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedReadout}
                  onChange={setSelectedReadout}
                  placeholder="Readout"
                />
                <MultiSelect
                  options={throughputOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedThroughput}
                  onChange={setSelectedThroughput}
                  placeholder="Throughput"
                />
                <MultiSelect
                  options={cellularResolutionOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedCellularResolution}
                  onChange={setSelectedCellularResolution}
                  placeholder="Cellular Resolution"
                />
                <MultiSelect
                  options={slideResolutionOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedSlideResolution}
                  onChange={setSelectedSlideResolution}
                  placeholder="Slide Resolution"
                />
                <MultiSelect
                  options={applicationOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedApplication}
                  onChange={setSelectedApplication}
                  placeholder="Application"
                />
                <MultiSelect
                  options={chemistryOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedChemistry}
                  onChange={setSelectedChemistry}
                  placeholder="Chemistry"
                />
                <MultiSelect
                  options={technologyOptions.map((option) => ({ label: option, value: option }))}
                  selected={selectedTechnology}
                  onChange={setSelectedTechnology}
                  placeholder="Technology"
                />
              </div>
            </div>
          </LazySection>

          {/* Active Filter Pills */}
          <ProductActiveFilters
            filters={[
              {
                key: 'Provider',
                label: 'Provider',
                options: providerOptions,
                selected: selectedProviders,
                setSelected: setSelectedProviders
              },
              {
                key: 'Sample Types',
                label: 'Sample Types',
                options: sampleTypeOptions,
                selected: selectedSampleTypes,
                setSelected: setSelectedSampleTypes
              },
              {
                key: 'Plex Type',
                label: 'Plex Types',
                options: plexTypeOptions,
                selected: selectedPlexTypes,
                setSelected: setSelectedPlexTypes
              },
              {
                key: 'Readout',
                label: 'Readout',
                options: readoutOptions,
                selected: selectedReadout,
                setSelected: setSelectedReadout
              },
              {
                key: 'Throughput',
                label: 'Throughput',
                options: throughputOptions,
                selected: selectedThroughput,
                setSelected: setSelectedThroughput
              },
              {
                key: 'Cellular Resolution',
                label: 'Cellular Resolution',
                options: cellularResolutionOptions,
                selected: selectedCellularResolution,
                setSelected: setSelectedCellularResolution
              },
              {
                key: 'Slide Resolution',
                label: 'Slide Resolution',
                options: slideResolutionOptions,
                selected: selectedSlideResolution,
                setSelected: setSelectedSlideResolution
              },
              {
                key: 'Application',
                label: 'Application',
                options: applicationOptions,
                selected: selectedApplication,
                setSelected: setSelectedApplication
              },
              {
                key: 'Chemistry',
                label: 'Chemistry',
                options: chemistryOptions,
                selected: selectedChemistry,
                setSelected: setSelectedChemistry
              },
              {
                key: 'Technology',
                label: 'Technology',
                options: technologyOptions,
                selected: selectedTechnology,
                setSelected: setSelectedTechnology
              }
            ]}
            onClearFilters={() => {
              setSelectedProviders([]);
              setSelectedSampleTypes([]);
              setSelectedPlexTypes([]);
              setSelectedReadout([]);
              setSelectedThroughput([]);
              setSelectedCellularResolution([]);
              setSelectedSlideResolution([]);
              setSelectedApplication([]);
              setSelectedChemistry([]);
              setSelectedTechnology([]);
            }}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
          
          {/* Product Sections */}
          
          {/* Instruments Section */}
          <div className="group relative mb-6">
            <div className="flex items-baseline">
              <h2 className="font-display text-3xl font-bold text-primary-500 group-hover:text-purple-500 transition-colors duration-300">Instruments</h2>
              <Link
                href="/products/instruments"
                className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-20px] group-hover:translate-x-0 inline-flex items-center ml-4 text-sm font-medium text-primary-600 group-hover:text-purple-600"
              >
                View All
                <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
          <ProductSection
            title=""
            products={groupedByProductType['Instruments'] || []}
            viewMode={viewMode}
            setViewMode={setViewMode}
            extractFields={extractFields}
            technologyLookup={technologyLookup}
            platformLookup={platformLookup}
            providerLookup={providerLookup}
            filters={[
              {
                key: 'Provider',
                label: 'Provider',
                options: providerOptions,
                selected: selectedProviders,
                setSelected: setSelectedProviders
              },
              {
                key: 'Sample Types',
                label: 'Sample Types',
                options: sampleTypeOptions,
                selected: selectedSampleTypes,
                setSelected: setSelectedSampleTypes
              },
              {
                key: 'Plex Type',
                label: 'Plex Types',
                options: plexTypeOptions,
                selected: selectedPlexTypes,
                setSelected: setSelectedPlexTypes
              },
              {
                key: 'Readout',
                label: 'Readout',
                options: readoutOptions,
                selected: selectedReadout,
                setSelected: setSelectedReadout
              },
              {
                key: 'Throughput',
                label: 'Throughput',
                options: throughputOptions,
                selected: selectedThroughput,
                setSelected: setSelectedThroughput
              },
              {
                key: 'Cellular Resolution',
                label: 'Cellular Resolution',
                options: cellularResolutionOptions,
                selected: selectedCellularResolution,
                setSelected: setSelectedCellularResolution
              },
              {
                key: 'Slide Resolution',
                label: 'Slide Resolution',
                options: slideResolutionOptions,
                selected: selectedSlideResolution,
                setSelected: setSelectedSlideResolution
              },
              {
                key: 'Application',
                label: 'Application',
                options: applicationOptions,
                selected: selectedApplication,
                setSelected: setSelectedApplication
              },
              {
                key: 'Chemistry',
                label: 'Chemistry',
                options: chemistryOptions,
                selected: selectedChemistry,
                setSelected: setSelectedChemistry
              },
              {
                key: 'Technology',
                label: 'Technology',
                options: technologyOptions,
                selected: selectedTechnology,
                setSelected: setSelectedTechnology
              }
            ]}
            getSubtypeOptions={getSubtypeOptions}
            selectedSubtype={selectedInstrumentType}
            setSelectedSubtype={setSelectedInstrumentType}
            productType="Instruments"
            showFilters={false}
          />

          {/* Assay Kits Section */}
          <div className="group relative mb-6">
            <div className="flex items-baseline">
              <h2 className="font-display text-3xl font-bold text-primary-500 group-hover:text-purple-500 transition-colors duration-300">Assay Kits</h2>
              <Link
                href="/products/assay-kits"
                className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-20px] group-hover:translate-x-0 inline-flex items-center ml-4 text-sm font-medium text-primary-600 group-hover:text-purple-600"
              >
                View All
                <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
          <ProductSection
            title=""
            products={groupedByProductType['Assay Kits'] || []}
            viewMode={viewMode}
            setViewMode={setViewMode}
            extractFields={extractFields}
            technologyLookup={technologyLookup}
            platformLookup={platformLookup}
            providerLookup={providerLookup}
            filters={[
              {
                key: 'Provider',
                label: 'Provider',
                options: providerOptions,
                selected: selectedProviders,
                setSelected: setSelectedProviders
              },
              {
                key: 'Sample Types',
                label: 'Sample Types',
                options: sampleTypeOptions,
                selected: selectedSampleTypes,
                setSelected: setSelectedSampleTypes
              },
              {
                key: 'Plex Type',
                label: 'Plex Types',
                options: plexTypeOptions,
                selected: selectedPlexTypes,
                setSelected: setSelectedPlexTypes
              },
              {
                key: 'Readout',
                label: 'Readout',
                options: readoutOptions,
                selected: selectedReadout,
                setSelected: setSelectedReadout
              },
              {
                key: 'Throughput',
                label: 'Throughput',
                options: throughputOptions,
                selected: selectedThroughput,
                setSelected: setSelectedThroughput
              },
              {
                key: 'Cellular Resolution',
                label: 'Cellular Resolution',
                options: cellularResolutionOptions,
                selected: selectedCellularResolution,
                setSelected: setSelectedCellularResolution
              },
              {
                key: 'Slide Resolution',
                label: 'Slide Resolution',
                options: slideResolutionOptions,
                selected: selectedSlideResolution,
                setSelected: setSelectedSlideResolution
              },
              {
                key: 'Application',
                label: 'Application',
                options: applicationOptions,
                selected: selectedApplication,
                setSelected: setSelectedApplication
              },
              {
                key: 'Chemistry',
                label: 'Chemistry',
                options: chemistryOptions,
                selected: selectedChemistry,
                setSelected: setSelectedChemistry
              },
              {
                key: 'Technology',
                label: 'Technology',
                options: technologyOptions,
                selected: selectedTechnology,
                setSelected: setSelectedTechnology
              }
            ]}
            getSubtypeOptions={getSubtypeOptions}
            selectedSubtype={selectedAssayType}
            setSelectedSubtype={setSelectedAssayType}
            productType="Assay Kits"
            showFilters={false}
          />

          {/* Assay Panels Section */}
          <div className="group relative mb-6">
            <div className="flex items-baseline">
              <h2 className="font-display text-3xl font-bold text-primary-500 group-hover:text-purple-500 transition-colors duration-300">Assay Panels</h2>
              <Link
                href="/products/assay-panels"
                className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-20px] group-hover:translate-x-0 inline-flex items-center ml-4 text-sm font-medium text-primary-600 group-hover:text-purple-600"
              >
                View All
                <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
          <ProductSection
            title=""
            products={groupedByProductType['Assay Panels'] || []}
            viewMode={viewMode}
            setViewMode={setViewMode}
            extractFields={extractFields}
            technologyLookup={technologyLookup}
            platformLookup={platformLookup}
            providerLookup={providerLookup}
            filters={[
              {
                key: 'Provider',
                label: 'Provider',
                options: providerOptions,
                selected: selectedProviders,
                setSelected: setSelectedProviders
              },
              {
                key: 'Sample Types',
                label: 'Sample Types',
                options: sampleTypeOptions,
                selected: selectedSampleTypes,
                setSelected: setSelectedSampleTypes
              },
              {
                key: 'Plex Type',
                label: 'Plex Types',
                options: plexTypeOptions,
                selected: selectedPlexTypes,
                setSelected: setSelectedPlexTypes
              },
              {
                key: 'Readout',
                label: 'Readout',
                options: readoutOptions,
                selected: selectedReadout,
                setSelected: setSelectedReadout
              },
              {
                key: 'Throughput',
                label: 'Throughput',
                options: throughputOptions,
                selected: selectedThroughput,
                setSelected: setSelectedThroughput
              },
              {
                key: 'Cellular Resolution',
                label: 'Cellular Resolution',
                options: cellularResolutionOptions,
                selected: selectedCellularResolution,
                setSelected: setSelectedCellularResolution
              },
              {
                key: 'Slide Resolution',
                label: 'Slide Resolution',
                options: slideResolutionOptions,
                selected: selectedSlideResolution,
                setSelected: setSelectedSlideResolution
              },
              {
                key: 'Application',
                label: 'Application',
                options: applicationOptions,
                selected: selectedApplication,
                setSelected: setSelectedApplication
              },
              {
                key: 'Chemistry',
                label: 'Chemistry',
                options: chemistryOptions,
                selected: selectedChemistry,
                setSelected: setSelectedChemistry
              },
              {
                key: 'Technology',
                label: 'Technology',
                options: technologyOptions,
                selected: selectedTechnology,
                setSelected: setSelectedTechnology
              }
            ]}
            getSubtypeOptions={getSubtypeOptions}
            selectedSubtype={selectedAssayPanelType}
            setSelectedSubtype={setSelectedAssayPanelType}
            productType="Assay Panels"
            showFilters={false}
          />

          {/* Software Section */}
          <div className="group relative mb-6">
            <div className="flex items-baseline">
              <h2 className="font-display text-3xl font-bold text-primary-500 group-hover:text-purple-500 transition-colors duration-300">Software</h2>
              <Link
                href="/products/software"
                className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-[-20px] group-hover:translate-x-0 inline-flex items-center ml-4 text-sm font-medium text-primary-600 group-hover:text-purple-600"
              >
                View All
                <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
          <ProductSection
            title=""
            products={groupedByProductType['Software'] || []}
            viewMode={viewMode}
            setViewMode={setViewMode}
            extractFields={extractFields}
            technologyLookup={technologyLookup}
            platformLookup={platformLookup}
            providerLookup={providerLookup}
            filters={[
              {
                key: 'Provider',
                label: 'Provider',
                options: providerOptions,
                selected: selectedProviders,
                setSelected: setSelectedProviders
              },
              {
                key: 'Platform',
                label: 'Platform',
                options: platformOptions,
                selected: selectedPlatforms,
                setSelected: setSelectedPlatforms
              },
              {
                key: 'Licence Type',
                label: 'Licence Type',
                options: licenceTypeOptions,
                selected: selectedLicenceTypes,
                setSelected: setSelectedLicenceTypes
              },
              {
                key: 'Interface',
                label: 'Interface',
                options: interfaceOptions,
                selected: selectedInterfaces,
                setSelected: setSelectedInterfaces
              }
            ]}
            getSubtypeOptions={getSubtypeOptions}
            selectedSubtype={selectedSoftwareType}
            setSelectedSubtype={setSelectedSoftwareType}
            productType="Software"
            showFilters={false}
          />
        </div>
      </div>
      <footer className="w-full py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2025 LabAtlas. All rights reserved.
    </div>
      </footer>
    </DirectoryProvider>
  );
} 